// Pure helpers for assembling the end-of-turn SSE emissions from the
// backend's `final` payload. No React / runtime imports — extracted from
// useTutorRuntime.js so the emission contract is unit-testable.
//
// Contract (Phase ε, memory/structured-output.md): `submit_reply` is the
// ONLY path to a user-visible reply. Free-form AIMessage text in the
// transcript — including the model's trailing repeat of the reply after
// the submit_reply round-trip — is NEVER user-visible. The wire messages
// are re-emitted solely to materialize tool-call cards in assistant-ui.

// Find the user-visible final AI reply in the wire messages: the LAST
// AI message that has text content but no tool_calls. Returns its
// index or -1.
export function findFinalReplyIdx(wireMessages) {
  if (!Array.isArray(wireMessages)) return -1;
  for (let i = wireMessages.length - 1; i >= 0; i--) {
    const m = wireMessages[i];
    if (m?.type !== 'ai') continue;
    const hasToolCalls = Array.isArray(m.tool_calls) && m.tool_calls.length > 0;
    const hasText = Array.isArray(m.content)
      ? m.content.some((p) => p?.type === 'text' && (p.text || '').trim())
      : typeof m.content === 'string' && m.content.trim();
    if (!hasToolCalls && hasText) return i;
  }
  return -1;
}

// Extract the text content from an AIMessage's content (may be string
// or block list).
export function aiTextOf(msg) {
  if (typeof msg?.content === 'string') return msg.content;
  if (Array.isArray(msg?.content)) {
    return msg.content
      .filter((p) => p?.type === 'text')
      .map((p) => p.text || '')
      .join('');
  }
  return '';
}

export function stripToolCallProse(m) {
  if (m?.type === 'ai' && Array.isArray(m.tool_calls) && m.tool_calls.length > 0) {
    return { ...m, content: [] };
  }
  return m;
}

function isPureTextAi(m) {
  return m?.type === 'ai' && !(Array.isArray(m.tool_calls) && m.tool_calls.length > 0);
}

export function pickFinalText(finalData) {
  const structured = typeof finalData?.reply === 'string' && finalData.reply.trim()
    ? finalData.reply
    : null;
  if (structured) return { text: structured, idx: -1 };
  const idx = findFinalReplyIdx(finalData.messages);
  if (idx < 0) return { text: null, idx: -1 };
  return { text: aiTextOf(finalData.messages[idx]), idx };
}

export function buildFinalEmissions({ messageId, finalData, yieldedAnyText, buildAi }) {
  const out = [];
  const messages = finalData?.messages;
  if (Array.isArray(messages) && messages.length > 0) {
    const { text: finalText, idx: finalIdx } = pickFinalText(finalData);
    if (finalText) {
      out.push({
        event: 'messages/partial',
        data: [{ id: messageId, type: 'ai', content: [{ type: 'text', text: finalText }] }],
      });
      // Re-emit the rest ONLY to materialize tool-call cards. Pure-text
      // AI messages are dropped outright: under the submit_reply contract
      // they are never user-visible, and the model's trailing free-form
      // repeat of the reply rendered as a duplicate bubble (prod bug,
      // 2026-06-10 — on the structured-reply path finalIdx is -1, so
      // `i !== finalIdx` filtered nothing and stripToolCallProse only
      // blanks tool-call messages).
      const others = messages
        .filter((m, i) => i !== finalIdx && !isPureTextAi(m))
        .map(stripToolCallProse);
      if (others.length > 0) {
        out.push({ event: 'messages/partial', data: others });
      }
    } else {
      out.push({ event: 'messages/partial', data: messages.map(stripToolCallProse) });
    }
  } else if (!yieldedAnyText && finalData?.reply) {
    out.push({ event: 'messages/partial', data: [buildAi(finalData.reply)] });
  }
  return out;
}

// Capture-tool side effects from a turn's `final` event. The panel
// renders proposal / pending_action / pending_curriculum_edit from
// these setters.
//
// Each `final` is the COMPLETE capture state for its turn — the backend
// wipes pending_action on every user message (tutor skill docs), so a
// final without one must CLEAR the prior turn's value. Conditional
// sets left stale buttons on screen pointing at server state the
// backend had already wiped (audit finding #1, 2026-06-10).
export function applyFinalCapture(data, { setProposal, setPendingAction, setPendingCurriculumEdit }) {
  setProposal(data?.proposal ?? null);
  setPendingAction(data?.pending_action ?? null);
  setPendingCurriculumEdit(data?.pending_curriculum_edit ?? null);
}

// Hidden thinking + live status line (issue #102 + frontier-chat
// hidden reasoning, 2026-06-11).
//
// Under the submit_reply contract EVERY streamed text delta is the
// model's working narration ("Now let me write comprehensive learning
// notes…") — the canonical reply only ever arrives in `final`. Frontier
// chat apps never print that narration; neither do we. The in-progress
// bubble shows exactly one quiet status line at a time:
//
//   narration delta  → THINKING_STATUS_TEXT   (generic, no content leak)
//   tools-node frame → TOOL_STATUS_TEXT       (the stronger #102 line)
//   narration again  → back to thinking        (the tool run is over —
//                                               deltas only flow while
//                                               the model is generating)
//   final            → the real reply replaces the shared-id bubble
//
// All helpers are pure state machines over `{ line }`; each transition
// emits at most once, so the drafting sub-agent's flood of frames
// (thousands per turn) never stacks lines.

export const INITIAL_STATUS_STATE = { line: null };

// The tool-execution node key in langchain's `create_agent` graph
// (tutor/agent.py). Confirm against a real logged `updates` frame if the
// graph topology changes — see issue #102 file pointers.
export const TOOL_NODE_KEYS = ['tools'];

// The single, generic, reassuring status line. NOT a debug trace — never
// names the tool or node (issue #102 out-of-scope).
export const TOOL_STATUS_TEXT =
  'working on it — drafting your notes can take a couple of minutes';

// The quiet generic line shown while the model narrates to itself.
// Never carries any of the narration's content.
export const THINKING_STATUS_TEXT = 'thinking…';

function lineEmission(messageId, text) {
  return {
    event: 'messages/partial',
    data: [{ id: messageId, type: 'ai', content: [{ type: 'text', text }] }],
  };
}

// Does this `updates` frame's payload name the tool-execution node?
export function updatesNamesToolNode(updatesData) {
  if (!updatesData || typeof updatesData !== 'object') return false;
  return Object.keys(updatesData).some((k) => TOOL_NODE_KEYS.includes(k));
}

// A narration text delta arrived. Show the thinking line unless it is
// already up; a delta after a tool run reverts the tool line to thinking
// (deltas only flow while the model is generating, so the tool is done).
export function emissionForNarration(state, { messageId }) {
  const cur = state || INITIAL_STATUS_STATE;
  if (cur.line === 'thinking') return { state: cur, emission: null };
  return {
    state: { line: 'thinking' },
    emission: lineEmission(messageId, THINKING_STATUS_TEXT),
  };
}

// Decide the status emission for an `updates` frame. Pure + idempotent
// per contiguous run: once the tool line is up, subsequent tool-node
// frames return emission=null so the drafting sub-agent's flood of node
// updates (thousands per turn — see the tutor_streaming.py heartbeat
// comment) never stacks N lines. A shown thinking line is UPGRADED.
export function statusEmissionForUpdates(state, updatesData, { messageId }) {
  const cur = state || INITIAL_STATUS_STATE;
  if (cur.line === 'tool') return { state: cur, emission: null };
  if (!updatesNamesToolNode(updatesData)) return { state: cur, emission: null };
  return {
    state: { line: 'tool' },
    emission: lineEmission(messageId, TOOL_STATUS_TEXT),
  };
}

// Clear the status line. Real content (the final reply) already REPLACES
// the shared-id bubble, so when `alreadyReplaced` is true we only reset
// state and emit nothing — no flicker, no empty bubble. The explicit
// empty emission is a safety net for the rare `final` that produced no
// replacement content at all (issue #102: the line must clear on `final`
// whether or not a status line was up).
export function clearStatusEmission(state, { messageId, alreadyReplaced }) {
  if (!state?.line || alreadyReplaced) {
    return { state: INITIAL_STATUS_STATE, emission: null };
  }
  return {
    state: INITIAL_STATUS_STATE,
    emission: lineEmission(messageId, ''),
  };
}

// Close out a turn the user ABORTED (stop button) while a status line
// was still up (issue #111). Both abort paths in useTutorRuntime return
// early WITHOUT clearing the shared-id bubble, so the last thing yielded
// — a 'thinking…' or 'working on it…' status line — would freeze as the
// bubble's permanent content. That is a lie: nothing is working on it.
//
// Pure state machine like clearStatusEmission. When a status line is up
// (`state.line === 'thinking' | 'tool'`) emit EXACTLY ONE quiet, empty,
// non-narration replacement for the shared-id bubble and reset state.
// When no line is up — real content was already yielded (a prior
// clearStatusEmission reset the line) or abort happened before any line
// — emit nothing so we never clobber the real reply or flash an empty
// bubble. Never carries narration (hidden-thinking contract, #110).
export function abortCloseout(state, { messageId }) {
  if (!state?.line) {
    return { state: INITIAL_STATUS_STATE, emission: null };
  }
  return {
    state: INITIAL_STATUS_STATE,
    emission: lineEmission(messageId, ''),
  };
}

// Close out a stream that ended WITHOUT a final event (#43: network
// drop, backend crash mid-turn). Never silent: replace the frozen
// partial with an explicit notice the user can act on. Aborted turns
// (stop button) stay quiet by design. Narration is never preserved —
// it was never user-visible content (hidden-thinking contract).
export function streamCloseout({ sawFinal, aborted, messageId }) {
  if (sawFinal || aborted) return null;
  return lineEmission(
    messageId,
    '⚠ The connection dropped before the tutor finished — send that again.',
  );
}
